/* Afina Mystic — theme.js
 * Single entry point. Every feature is an init function that receives a root
 * (defaults to document) so Shopify Theme Editor can re-initialize a section
 * when it's added, moved, or edited without a full page reload.
 */
(function () {
  'use strict';

  const qsa = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const qs = (sel, root) => (root || document).querySelector(sel);

  /* ── Header height → CSS var ──────────────────────────────────────────── */
  function initHeaderOffset() {
    const header = qs('.header');
    const announce = qs('.section-announcement');
    if (!header && !announce) return;
    const set = () => {
      if (announce) document.documentElement.style.setProperty('--announce-height', announce.offsetHeight + 'px');
      if (header) document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    set();
    window.addEventListener('resize', set, { passive: true });
    if (announce && 'ResizeObserver' in window) new ResizeObserver(set).observe(announce);
  }

  /* ── FAQ accordion ────────────────────────────────────────────────────── */
  function initFaq(root) {
    qsa('.faq__list-item', root).forEach((item) => {
      if (item.__faqBound) return;
      item.__faqBound = true;
      const trigger = item.querySelector('.faq__list-item-top');
      const panel = item.querySelector('.faq__list-item-bottom');
      if (!trigger || !panel) return;
      item.addEventListener('click', () => {
        const open = item.classList.toggle('active');
        trigger.setAttribute('aria-expanded', String(open));
      });
    });

    qsa('.faq', root).forEach((faq) => {
      const first = faq.querySelector('.faq__list-item');
      if (first && !first.classList.contains('active')) {
        first.classList.add('active');
        const trig = first.querySelector('.faq__list-item-top');
        if (trig) trig.setAttribute('aria-expanded', 'true');
      }
    });
  }

  /* ── Dropdowns (language/currency/custom) ────────────────────────────── */
  function initDropdowns(root) {
    qsa('.dropdown', root).forEach((dropdown) => {
      if (dropdown.__ddBound) return;
      dropdown.__ddBound = true;
      const trigger = dropdown.querySelector('.dropdown__trigger');
      const list = dropdown.querySelector('.dropdown__list');
      const label = dropdown.querySelector('.dropdown__selected');
      if (!trigger || !list) return;
      const items = qsa('.dropdown__item', list);

      function selectItem(item) {
        items.forEach((i) => {
          i.classList.remove('dropdown__item--active');
          i.setAttribute('aria-selected', 'false');
          i.hidden = false;
        });
        item.classList.add('dropdown__item--active');
        item.setAttribute('aria-selected', 'true');
        item.hidden = items.length > 1;
        if (label) label.textContent = item.textContent.trim();
      }

      const def = list.querySelector('.dropdown__item--active') || items[0];
      if (def) selectItem(def);

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = dropdown.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', String(open));
      });

      list.addEventListener('click', (e) => {
        const item = e.target.closest('.dropdown__item');
        if (!item) return;
        // Letting the <button> inside submit the localization form naturally.
        selectItem(item);
        dropdown.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      });
    });
  }
  document.addEventListener('click', () => {
    qsa('.dropdown.is-open').forEach((dd) => {
      dd.classList.remove('is-open');
      const t = dd.querySelector('.dropdown__trigger');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  });

  /* ── Burger menu ──────────────────────────────────────────────────────── */
  function initBurger() {
    const btn = document.getElementById('burgerBtn');
    const nav = document.getElementById('mobileNav');
    const head = btn && btn.closest('.header');
    if (!btn || !nav || !head || btn.__burgerBound) return;
    btn.__burgerBound = true;
    btn.addEventListener('click', () => {
      const open = head.classList.toggle('is-menu-open');
      btn.setAttribute('aria-expanded', String(open));
      nav.setAttribute('aria-hidden', String(!open));
      document.body.style.overflow = open ? 'hidden' : '';
    });
  }

  /* ── Announcement bar — manual horizontal scroll, no autoplay ─────────── */
  function initAnnouncementBar(root) {
    qsa('.header__adv-list', root).forEach((list) => {
      if (list.__advBound) return;
      list.__advBound = true;

      let dragging = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      list.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        if (list.scrollWidth <= list.clientWidth) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = list.scrollLeft;
        list.classList.add('is-dragging');
      });

      window.addEventListener('mousemove', (e) => {
        if (!dragging) return;
        const delta = e.clientX - startX;
        if (!moved && Math.abs(delta) > 5) moved = true;
        if (moved) {
          e.preventDefault();
          list.scrollLeft = startScroll - delta;
        }
      });

      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        list.classList.remove('is-dragging');
      };
      window.addEventListener('mouseup', endDrag);

      list.addEventListener('click', (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
    });
  }

  /* ── Testimonial strip slider with dot pagination + infinite loop ────── */
  function initRealSlider(root) {
    qsa('[data-real-slider]', root).forEach((slider) => {
      if (slider.__realBound) return;
      slider.__realBound = true;

      const track = slider.querySelector('[data-real-track]');
      const dotsWrap = slider.querySelector('[data-real-dots]');
      if (!track || !dotsWrap) return;

      const originals = Array.from(track.children);
      const N = originals.length;
      if (N <= 1) { dotsWrap.style.display = 'none'; return; }

      const cloneCount = 0;
      const cloneSlide = (el) => {
        const c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.classList.add('real__slide--clone');
        c.removeAttribute('id');
        const v = c.querySelector('video');
        if (v) v.setAttribute('preload', 'none');
        return c;
      };

      const headClones = [];
      for (let i = N - cloneCount; i < N; i++) headClones.push(cloneSlide(originals[i]));
      const tailClones = [];
      for (let i = 0; i < cloneCount; i++) tailClones.push(cloneSlide(originals[i]));

      for (let i = headClones.length - 1; i >= 0; i--) {
        track.insertBefore(headClones[i], track.firstChild);
      }
      tailClones.forEach((c) => track.appendChild(c));

      const slides = Array.from(track.children);
      const firstReal = cloneCount;
      const lastReal = cloneCount + N - 1;

      dotsWrap.innerHTML = '';
      const dots = [];
      for (let i = 0; i < N; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'real__dot';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
        btn.addEventListener('click', () => goTo(i, true));
        dotsWrap.appendChild(btn);
        dots.push(btn);
      }

      let activeIdx = -1;
      const setActive = (i) => {
        if (i === activeIdx) return;
        activeIdx = i;
        dots.forEach((d, j) => {
          if (j === i) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      };

      const slideLeft = (globalIdx) => slides[globalIdx].offsetLeft - track.offsetLeft;

      const scrollInstant = (left) => {
        const prev = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = left;
        requestAnimationFrame(() => { track.style.scrollBehavior = prev; });
      };

      const goTo = (realIdx, smooth) => {
        const left = slideLeft(firstReal + realIdx);
        if (smooth) track.scrollTo({ left, behavior: 'smooth' });
        else scrollInstant(left);
      };

      requestAnimationFrame(() => goTo(0, false));

      const findClosest = () => {
        const trackLeft = track.getBoundingClientRect().left;
        let best = firstReal, bestDist = Infinity;
        for (let i = 0; i < slides.length; i++) {
          const d = Math.abs(slides[i].getBoundingClientRect().left - trackLeft);
          if (d < bestDist) { bestDist = d; best = i; }
        }
        return best;
      };

      const realIndexFromGlobal = (g) => {
        if (g < firstReal) return N - cloneCount + g;
        if (g > lastReal) return g - cloneCount - N;
        return g - firstReal;
      };

      let teleporting = false;
      const teleportIfNeeded = () => {
        if (teleporting) return;
        const g = findClosest();
        if (g >= firstReal && g <= lastReal) return;
        teleporting = true;
        const realIdx = realIndexFromGlobal(g);
        scrollInstant(slideLeft(firstReal + realIdx));
        requestAnimationFrame(() => { teleporting = false; });
      };

      let rafPending = false;
      let scrollEndTimer = null;
      const onScroll = () => {
        if (!rafPending) {
          rafPending = true;
          requestAnimationFrame(() => {
            rafPending = false;
            setActive(realIndexFromGlobal(findClosest()));
          });
        }
        if (scrollEndTimer) clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(teleportIfNeeded, 140);
      };

      track.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', () => goTo(activeIdx >= 0 ? activeIdx : 0, false));

      // Mouse drag-to-scroll
      let dragging = false, startX = 0, startScroll = 0, moved = false;
      const dragThreshold = 5;

      track.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        track.classList.add('is-dragging');
        track.style.scrollBehavior = 'auto';
      });

      window.addEventListener('mousemove', (e) => {
        if (!dragging) return;
        const delta = e.clientX - startX;
        if (!moved && Math.abs(delta) > dragThreshold) moved = true;
        if (moved) {
          e.preventDefault();
          track.scrollLeft = startScroll - delta;
        }
      });

      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        track.style.scrollBehavior = '';
        if (moved) {
          window.__afinaSuppressClick = true;
          const realIdx = realIndexFromGlobal(findClosest());
          goTo(realIdx, true);
        }
      };

      window.addEventListener('mouseup', endDrag);
      window.addEventListener('blur', endDrag);
      track.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  /* ── Product gallery ─────────────────────────────────────────────────── */
  function initProductGallery(root) {
    qsa('.product-gallery', root).forEach((gallery) => {
      if (gallery.__galleryBound) return;
      gallery.__galleryBound = true;

      const main = gallery.querySelector('.product-gallery__main');
      const track = gallery.querySelector('.product-gallery__track');
      const slides = qsa('.product-gallery__slide', gallery);
      const thumbs = qsa('.product-gallery__thumb', gallery);
      const prev = gallery.querySelector('.product-gallery__arrow--prev');
      const next = gallery.querySelector('.product-gallery__arrow--next');
      if (!main || !track || !slides.length) return;

      let index = slides.findIndex((s) => s.classList.contains('is-active'));
      if (index < 0) index = thumbs.findIndex((t) => t.classList.contains('is-active'));
      if (index < 0) index = 0;

      const TRANSITION = 'transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1)';

      function snap(animate) {
        track.style.transition = animate === false ? 'none' : TRANSITION;
        track.style.transform = `translate3d(${-index * main.offsetWidth}px, 0, 0)`;
      }

      function setActive(i) {
        index = ((i % slides.length) + slides.length) % slides.length;
        slides.forEach((s, j) => s.classList.toggle('is-active', j === index));
        thumbs.forEach((t, j) => t.classList.toggle('is-active', j === index));
        snap(true);
      }

      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => setActive(i)));
      prev && prev.addEventListener('click', () => setActive(index - 1));
      next && next.addEventListener('click', () => setActive(index + 1));

      window.addEventListener('resize', () => snap(false), { passive: true });

      /* ── Swipe / drag with live track translation ─────────────────────
       * Pointer events handle mouse + touch + pen with one code path.
       * The track follows the finger horizontally; on release it snaps
       * to the next/previous slide if the drag passes the threshold,
       * otherwise back to the current slide.
       */
      if (slides.length > 1) {
        main.style.touchAction = 'pan-y';
        main.style.userSelect = 'none';
        main.style.webkitUserSelect = 'none';

        let startX = 0, startY = 0, dx = 0, dy = 0;
        let dragging = false, locked = false, axis = null, pid = null;
        let baseX = 0;

        const SWIPE_MIN = 40;
        const SWIPE_RATIO = 0.15;
        const AXIS_LOCK = 8;

        main.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (e.target.closest('.product-gallery__arrow')) return;
          dragging = true; locked = false; axis = null;
          startX = e.clientX; startY = e.clientY;
          dx = 0; dy = 0;
          pid = e.pointerId;
          baseX = -index * main.offsetWidth;
          track.style.transition = 'none';
        });

        main.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          dx = e.clientX - startX;
          dy = e.clientY - startY;

          if (!locked && (Math.abs(dx) > AXIS_LOCK || Math.abs(dy) > AXIS_LOCK)) {
            axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
            locked = true;
            if (axis === 'x') {
              try { main.setPointerCapture(pid); } catch (_) { }
            }
          }

          if (axis === 'x') {
            // Resistance at the ends so it feels alive without overshooting.
            let pull = dx;
            if ((index === 0 && dx > 0) || (index === slides.length - 1 && dx < 0)) {
              pull = dx * 0.35;
            }
            track.style.transform = `translate3d(${baseX + pull}px, 0, 0)`;
            e.preventDefault();
          }
        });

        function endSwipe() {
          if (!dragging) return;
          const wasAxisX = axis === 'x';
          dragging = false;
          if (pid != null) { try { main.releasePointerCapture(pid); } catch (_) { } pid = null; }

          if (!wasAxisX) {
            track.style.transition = TRANSITION;
            return;
          }

          const threshold = Math.max(SWIPE_MIN, main.offsetWidth * SWIPE_RATIO);
          if (dx <= -threshold && index < slides.length - 1) setActive(index + 1);
          else if (dx >= threshold && index > 0) setActive(index - 1);
          else snap(true);
        }

        main.addEventListener('pointerup', endSwipe);
        main.addEventListener('pointercancel', endSwipe);
        main.addEventListener('dragstart', (e) => e.preventDefault());
      }

      // Re-snap once layout settles (fonts/images can shift widths).
      requestAnimationFrame(() => snap(false));
    });
  }

  /* ── Reviews slider (767px+) ─────────────────────────────────────────── */
  function initReviewsSlider(root) {
    qsa('.reviews-slider', root).forEach((slider) => {
      if (slider.__reviewsBound) return;
      slider.__reviewsBound = true;
      const track = slider.querySelector('.reviews-slider__track');
      const slides = qsa('.reviews-slider__slide', slider);
      const dotsW = slider.querySelector('.reviews-slider__dots');
      if (!track || slides.length < 2 || !dotsW) return;

      const GAP = 15;
      let index = 0, startX = 0, delta = 0, dragging = false, pid = null;

      const dots = slides.map((_, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'reviews-slider__dot';
        d.setAttribute('aria-label', `Go to review ${i + 1}`);
        d.addEventListener('click', () => go(i));
        dotsW.appendChild(d);
        return d;
      });

      const step = () => slides[0].offsetWidth + GAP;
      function update() {
        track.style.transform = `translate3d(${-index * step()}px,0,0)`;
        dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      }
      function go(i) { index = (i + slides.length) % slides.length; update(); }

      track.addEventListener('pointerdown', (e) => {
        dragging = true; pid = e.pointerId; startX = e.clientX; delta = 0;
        track.classList.add('is-dragging'); track.setPointerCapture(pid);
      });
      track.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        delta = e.clientX - startX;
        track.style.transform = `translate3d(${-index * step() + delta}px,0,0)`;
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        if (pid != null) { try { track.releasePointerCapture(pid); } catch (_) { } pid = null; }
        const threshold = slides[0].offsetWidth * 0.15;
        if (delta <= -threshold) go(index + 1);
        else if (delta >= threshold) go(index - 1);
        else update();
        delta = 0;
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('pointerleave', endDrag);
      track.addEventListener('dragstart', (e) => e.preventDefault());
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ── Video click-to-play ─────────────────────────────────────────────── */
  function initVideoToggle() {
    if (document.__videoBound) return;
    document.__videoBound = true;
    document.addEventListener('click', (e) => {
      if (window.__afinaSuppressClick) { window.__afinaSuppressClick = false; return; }
      const wrap = e.target.closest('.video-wrap');
      if (!wrap) return;
      const v = wrap.querySelector('video');
      if (!v) return;
      if (v.paused) v.play().catch(() => { }); else v.pause();
    });
    document.addEventListener('play', (e) => {
      if (!(e.target instanceof HTMLVideoElement)) return;
      const w = e.target.closest('.video-wrap');
      if (w) w.classList.add('is-playing');
    }, true);
    document.addEventListener('pause', (e) => {
      if (!(e.target instanceof HTMLVideoElement)) return;
      const w = e.target.closest('.video-wrap');
      if (w) w.classList.remove('is-playing');
    }, true);
  }

  /* ── Product variant picker → single select → cart ───────────────────── */
  function initProductForm(root) {
    qsa('product-form, .product-form', root).forEach((form) => {
      if (form.__pfBound) return;
      form.__pfBound = true;

      const select = form.querySelector('[data-variant-select]');
      const hidden = form.querySelector('[data-variant-id]');
      const buttons = qsa('.product__info-option', form);
      const priceBox = form.querySelector('[data-price-wrapper]') || form.closest('.product__info').querySelector('[data-price-wrapper]');

      function sync(variantId) {
        if (!variantId) return;
        if (hidden) hidden.value = variantId;
        if (select) select.value = variantId;
      }

      if (select) {
        select.addEventListener('change', () => sync(select.value));
        sync(select.value);
      }

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const group = btn.closest('.product__info-options-toggle');
          qsa('.product__info-option', group).forEach((b) => {
            b.classList.remove('is-active');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('is-active');
          btn.setAttribute('aria-checked', 'true');

          // Rebuild a selected value list per option index → pick variant
          if (!select) return;
          const values = qsa('.product__info-options-group', form).map((group) => {
            const active = group.querySelector('.product__info-option.is-active');
            return active ? active.dataset.optionValue : null;
          });
          const match = Array.from(select.options).find((opt) => {
            const title = opt.textContent.split('—')[0].trim();
            return values.every((v) => v == null || title.indexOf(v) > -1);
          });
          if (match) { select.value = match.value; sync(match.value); select.dispatchEvent(new Event('change')); }
        });
      });
    });
  }
  
  

  /* ── Section Editor support: re-init on Shopify events ───────────────── */
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:unload', () => { });
  document.addEventListener('shopify:block:select', (e) => {
    const item = e.target.closest('.faq__list-item');
    if (item && !item.classList.contains('active')) item.classList.add('active');
  });

  /* ── Boot ─────────────────────────────────────────────────────────────── */
  function boot(root) {
    initHeaderOffset();
    initBurger();
    initDropdowns(root);
    initFaq(root);
    initAnnouncementBar(root);
    initRealSlider(root);
    initProductGallery(root);
    initReviewsSlider(root);
    initProductForm(root);
    initVideoToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }
})();